import React from 'react';
import LudoBoard from './LudoBoard';
import WaitingRoom from '../WaitingRoom';
import { useLudoGame } from '../../hooks/useLudoGame';
import { useLegacyGameState } from '../../hooks/useLegacyGameState';
import { useGameInteraction } from '../../hooks/useGameInteraction';
import './LudoGame.css';
import './SecureLudoGame.css';

interface SecureLudoGameProps {
  gameId?: string | null;
}

const SecureLudoGame: React.FC<SecureLudoGameProps> = ({
  gameId: directGameId,
}) => {
  // Core game logic
  const {
    gameId,
    backendGameState,
    availableMoves,
    isLoading,
    error,
    isRolling,
    celebratingPiece,
    startGame,
    rollDice,
    movePiece,
    refreshGameState,
    setError,
  } = useLudoGame({ gameType: 'classic-ludo', gameId: directGameId });

  // Legacy state conversion for board compatibility
  const { legacyGameState } = useLegacyGameState({
    backendGameState,
    availableMoves,
  });

  // Game interaction handlers
  const { handlePieceClick, handleSquareClick } = useGameInteraction({
    backendGameState,
    legacyGameState,
    availableMoves,
    movePiece,
    setError,
  });

  // Development environment detection
  const isDevelopment =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  // Loading state
  if (isLoading && !backendGameState) {
    return (
      <div className="secure-ludo-game">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Initializing game...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="secure-ludo-game">
        <div className="error-container">
          <h3>Game Error</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reload Game</button>
        </div>
      </div>
    );
  }

  // Waiting room (game not started)
  if (backendGameState?.gamePhase === 'waiting') {
    return (
      <div className="secure-ludo-game">
        <WaitingRoom
          gameType="classic-ludo"
          gameId={gameId || ''}
          playersConnected={backendGameState.players.length}
          maxPlayers={4}
          isLoading={isLoading}
          onStartGame={startGame}
          playerNames={backendGameState.players.map((p: any) => p.displayName)}
        />
      </div>
    );
  }

  // Main game interface
  return (
    <div className="secure-ludo-game">
      {/* Game Header */}
      <div className="game-header">
        <h2>Secure Ludo Game</h2>
        {gameId && (
          <div className="game-info">
            <span>Game ID: {gameId}</span>
            <button onClick={refreshGameState} className="refresh-btn">
              🔄 Refresh
            </button>
          </div>
        )}
      </div>

      {/* Current Player Info */}
      {backendGameState && (
        <div className="current-player-info">
          <h3>
            Current Player:{' '}
            {
              backendGameState.players[backendGameState.currentPlayerIndex]
                ?.displayName
            }
          </h3>
          {legacyGameState.diceValue && (
            <div className="dice-result">
              Last Roll: {legacyGameState.diceValue}
            </div>
          )}
        </div>
      )}

      {/* Game Controls */}
      <div className="game-controls">
        <button
          onClick={rollDice}
          disabled={isRolling || !backendGameState}
          className={`roll-dice-btn ${isRolling ? 'rolling' : ''}`}
        >
          {isRolling ? 'Rolling...' : 'Roll Dice'}
        </button>
      </div>

      {/* Game Board */}
      <div className="game-board-container">
        <LudoBoard
          gameState={legacyGameState}
          onPieceClick={handlePieceClick}
          onSquareClick={handleSquareClick}
          celebratingPiece={celebratingPiece}
        />
      </div>

      {/* Game Status */}
      {legacyGameState.gameStatus === 'finished' && legacyGameState.winner && (
        <div className="game-finished">
          <h2>🎉 Game Finished! 🎉</h2>
          <p>Winner: {legacyGameState.winner}</p>
          <button onClick={() => window.location.reload()}>Play Again</button>
        </div>
      )}

      {/* Debug Info (Development Only) */}
      {isDevelopment && (
        <div className="debug-info">
          <h4>Debug Info</h4>
          <details>
            <summary>Backend Game State</summary>
            <pre>{JSON.stringify(backendGameState, null, 2)}</pre>
          </details>
          <details>
            <summary>Available Moves</summary>
            <pre>{JSON.stringify(availableMoves, null, 2)}</pre>
          </details>
          <details>
            <summary>Legacy Game State</summary>
            <pre>{JSON.stringify(legacyGameState, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default SecureLudoGame;
